<template>
  <div class="cashier-desktop cashier-desktop--wechat">
    <div class="cashier-desktop__shell cashier-desktop__shell--split">
      <aside class="cashier-desktop__overview">
        <div class="cashier-desktop__badge">
          <span class="cashier-desktop__badge-dot"></span>
          微信支付
        </div>
        <div class="cashier-desktop__amount">¥{{ formattedAmount }}</div>
        <h1 class="cashier-desktop__title">{{ payOrderInfo.subject || '微信支付订单' }}</h1>
        <div class="cashier-desktop__subtitle">{{ payOrderInfo.mchName || '商户信息加载中' }}</div>
        <div v-if="payStatusText" class="cashier-desktop__status-chip">{{ payStatusText }}</div>

        <div class="cashier-desktop__overview-grid">
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">支付方式</div>
            <div class="cashier-desktop__meta-value">{{ methodName }}</div>
          </div>
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">订单状态</div>
            <div class="cashier-desktop__meta-value cashier-desktop__meta-value--status">{{ orderStateText }}</div>
          </div>
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">订单号</div>
            <div class="cashier-desktop__meta-value">{{ payOrderInfo.payOrderId || '--' }}</div>
          </div>
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">商户单号</div>
            <div class="cashier-desktop__meta-value">{{ payOrderInfo.mchOrderNo || '--' }}</div>
          </div>
        </div>

        <div class="cashier-desktop__overview-copy">
          <strong>{{ isQrPayMode ? '请使用微信扫码完成支付。' : '请在微信中确认支付。' }}</strong>
        </div>
      </aside>

      <section class="cashier-desktop__workspace">
        <div class="cashier-desktop__workspace-main">
          <div class="cashier-desktop__section-head">
            <div>
              <h2 class="cashier-desktop__section-title">请完成支付</h2>
            </div>
          </div>

          <div class="cashier-desktop__qr-card">
            <div>
              <h3 class="cashier-desktop__qr-title">{{ isQrPayMode ? '扫描二维码支付' : '正在发起微信支付' }}</h3>
              <p class="cashier-desktop__qr-copy">{{ isQrPayMode ? '请打开微信扫一扫二维码完成付款。' : '支付请求创建后会自动继续。' }}</p>
            </div>

            <div class="cashier-desktop__qr-stage">
              <div class="cashier-desktop__qr-frame">
                <template v-if="qrCodeUrl">
                  <img v-if="qrCodeImageMode" :src="qrCodeUrl" alt="微信支付二维码" />
                  <div v-else ref="qrcode"></div>
                </template>
                <div v-else class="cashier-desktop__placeholder">
                  <div class="cashier-desktop__placeholder-mark">¥</div>
                  <div class="cashier-desktop__placeholder-title">{{ placeholderTitle }}</div>
                  <div class="cashier-desktop__placeholder-copy">{{ placeholderCopy }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="cashier-desktop__workspace-side">
          <div class="cashier-desktop__summary">
            <div class="cashier-desktop__summary-head">
              <h3 class="cashier-desktop__card-title">订单摘要</h3>
            </div>
            <div class="cashier-desktop__summary-row">
              <span class="cashier-desktop__summary-key">订单号</span>
              <span class="cashier-desktop__summary-value">{{ payOrderInfo.payOrderId || '--' }}</span>
            </div>
            <div class="cashier-desktop__summary-row">
              <span class="cashier-desktop__summary-key">商户单号</span>
              <span class="cashier-desktop__summary-value">{{ payOrderInfo.mchOrderNo || '--' }}</span>
            </div>
            <div class="cashier-desktop__summary-row">
              <span class="cashier-desktop__summary-key">状态</span>
              <span class="cashier-desktop__summary-value">{{ orderStateText }}</span>
            </div>
          </div>

          <div v-if="productItems.length > 0" class="cashier-desktop__products">
            <div class="cashier-desktop__products-head">
              <h3 class="cashier-desktop__card-title">商品明细</h3>
            </div>

            <div class="cashier-desktop__product-list">
              <div class="cashier-desktop__product-item" v-for="(item, index) in productItems" :key="index">
                <div>
                  <div class="cashier-desktop__product-name">{{ item.name }}</div>
                  <div class="cashier-desktop__product-count">x{{ item.quantity }}</div>
                </div>
                <div class="cashier-desktop__product-price">¥{{ formatAmount(item.price) }}</div>
              </div>
            </div>
          </div>

          <div class="cashier-desktop__actions">
            <div class="cashier-desktop__actions-head">
              <h3 class="cashier-desktop__card-title">操作</h3>
            </div>

            <div class="cashier-desktop__actions-row">
              <button class="cashier-desktop__primary-btn" :disabled="isPaySubmitting" @click="pay">
                {{ actionButtonText }}
              </button>
              <button class="cashier-desktop__secondary-btn" type="button" @click="$router.push({ name: 'CashierHome' })">
                返回选择
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcodejs2'
import { getPayPackage, getPayOrderInfo, getRedirectUrl } from '@/api/api'
import { getCachedPayData, removeCachedPayData, setCachedPayData } from '@/utils/cashierPayCache'
import channelUserIdUtil from '@/utils/channelUserId'
import wayCodeUtil from '@/utils/wayCode'
import config from '@/config'

export default {
  data: function () {
    return {
      amount: null,
      resData: {},
      payOrderInfo: {},
      productItems: [],
      qrCodeUrl: null,
      qrCodeImageMode: false,
      isPaySubmitting: false,
      payStatusText: '',
      statusPollTimer: null,
      isSuccessHandled: false
    }
  },
  computed: {
    currentWayCode() {
      const payWay = wayCodeUtil.getPayWay()
      return payWay ? payWay.wayCode : 'WX_NATIVE'
    },
    methodName() {
      return this.currentWayCode === 'WX_JSAPI' ? '微信内支付' : '微信扫码支付'
    },
    isQrPayMode() {
      return this.currentWayCode === 'WX_NATIVE'
    },
    formattedAmount() {
      return this.formatAmount(this.payOrderInfo.amount)
    },
    orderStateText() {
      const state = this.payOrderInfo.state
      if (state === 2 || state === '2') return '支付成功'
      if (state === 1 || state === '1') return '支付中'
      if (state === 3 || state === '3') return '支付失败'
      if (state === 6 || state === '6') return '订单关闭'
      return '待支付'
    },
    placeholderTitle() {
      if (this.currentWayCode === 'WX_JSAPI') {
        return '正在进入微信支付'
      }
      return '二维码生成中'
    },
    placeholderCopy() {
      if (this.currentWayCode === 'WX_JSAPI') {
        return '即将打开微信支付'
      }
      return '二维码准备完成后即可扫码'
    },
    actionButtonText() {
      if (this.isPaySubmitting) {
        return '处理中...'
      }
      if (this.currentWayCode === 'WX_JSAPI') {
        return '重新发起支付'
      }
      return '刷新二维码'
    }
  },
  mounted() {
    this.setPayOrderInfo()
  },
  beforeDestroy() {
    this.stopStatusPolling()
  },
  methods: {
    formatAmount(amount) {
      if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
        return '--'
      }
      return (Number(amount) / 100).toFixed(2)
    },

    syncProductItems(extParam) {
      this.productItems = []
      if (!extParam) return
      try {
        const extData = JSON.parse(extParam)
        if (Array.isArray(extData.items)) {
          this.productItems = extData.items
        }
      } catch (e) {
        console.error('解析extParam失败:', e)
      }
    },

    setPayOrderInfo() {
      const that = this
      getPayOrderInfo().then(res => {
        that.payOrderInfo = res
        that.syncProductItems(res.extParam)

        if (res.amount) {
          that.amount = res.amount / 100
          if (res.state === 2 || res.state === '2') {
            that.clearCachedPayData()
            that.handlePaySuccess(res)
          } else if (res.state === 1 || res.state === '1') {
            if (that.restoreCachedPayData()) {
              return
            }
            that.startStatusPolling()
            that.payStatusText = that.isQrPayMode
              ? '当前订单支付中，请继续扫码支付'
              : '支付请求已创建，请继续完成支付'
          } else if (res.state === 3 || res.state === '3' || res.state === 6 || res.state === '6') {
            that.clearCachedPayData()
            that.payStatusText = res.errMsg || '订单状态异常'
          } else if (res.state === 0 || res.state === '0' || res.state === undefined || res.state === null) {
            that.pay()
          }
        }
      }).catch(res => {
        that.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      })
    },

    pay: function () {
      if (this.isPaySubmitting) {
        return
      }

      if (isNaN(this.amount) || this.amount <= 0) {
        this.payStatusText = '请输入有效支付金额'
        return alert('请输入金额')
      }

      if (this.isQrPayMode && (this.payOrderInfo.state === 1 || this.payOrderInfo.state === '1')) {
        if (this.restoreCachedPayData()) {
          this.payStatusText = '已恢复当前订单二维码'
        } else {
          this.startStatusPolling()
          this.payStatusText = '当前订单已支付中，请勿重复发起支付'
        }
        return
      }

      const currentWayCode = this.currentWayCode
      if (wayCodeUtil.requiresChannelUserId(currentWayCode) && !channelUserIdUtil.getChannelUserId()) {
        this.payStatusText = '正在跳转微信授权环境...'
        return getRedirectUrl().then(url => {
          window.location.href = url
        }).catch(res => {
          this.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
        })
      }

      const that = this
      that.isPaySubmitting = true
      that.qrCodeUrl = null
      that.qrCodeImageMode = false
      that.payStatusText = currentWayCode === 'WX_JSAPI' ? '正在创建支付请求...' : '正在生成二维码...'
      that.isSuccessHandled = false
      that.stopStatusPolling()
      if (that.$refs.qrcode) {
        that.$refs.qrcode.innerHTML = ''
      }

      getPayPackage(this.amount).then(res => {
        if (String(res.code) !== '0') {
          that.payStatusText = res.msg || '创建支付请求失败'
          return alert(res.msg)
        }

        if (Number(res.data.orderState) !== 1) {
          that.payStatusText = res.data.errMsg || '订单状态异常'
          return alert(res.data.errMsg)
        }

        that.resData = res.data

        const normalizedPayData = that.normalizePayData(res.data)
        if (that.handlePayData(normalizedPayData)) {
          return
        }

        that.payStatusText = '正在等待微信确认...'
        if (typeof window.WeixinJSBridge === 'undefined') {
          if (document.addEventListener) {
            document.addEventListener('WeixinJSBridgeReady', that.onBridgeReady, false)
          } else if (document.attachEvent) {
            document.attachEvent('WeixinJSBridgeReady', that.onBridgeReady)
            document.attachEvent('onWeixinJSBridgeReady', that.onBridgeReady)
          }
        } else {
          that.onBridgeReady()
        }
      }).catch(res => {
        that.$router.push({ name: config.errorPageRouteName, params: { errInfo: res.msg } })
      }).finally(() => {
        that.isPaySubmitting = false
      })
    },

    normalizePayData(payData) {
      if (!payData) return null
      const normalized = Object.assign({}, payData)
      normalized.payDataType = normalized.payDataType || (
        normalized.codeImgUrl ? 'codeImgUrl'
          : normalized.codeUrl ? 'codeUrl'
            : normalized.payUrl ? 'payurl'
              : normalized.formContent ? 'form' : null
      )
      normalized.payData = normalized.payData || normalized.codeImgUrl || normalized.codeUrl || normalized.payUrl || normalized.formContent || null
      return normalized
    },

    getPayCacheKey() {
      const payOrderId = this.payOrderInfo.payOrderId || config.cacheToken
      return payOrderId ? `${this.currentWayCode}:${payOrderId}` : null
    },

    restoreCachedPayData() {
      if (!this.isQrPayMode) {
        return false
      }

      const cacheKey = this.getPayCacheKey()
      const payData = getCachedPayData(cacheKey) || this.getPersistedPayData()
      if (!payData) {
        return false
      }

      return this.handlePayData(this.normalizePayData(payData))
    },

    getPersistedPayData() {
      if (!this.payOrderInfo.channelExtra) {
        return null
      }

      try {
        const channelExtra = JSON.parse(this.payOrderInfo.channelExtra)
        return channelExtra && channelExtra.cashierPayData ? channelExtra.cashierPayData : null
      } catch (e) {
        return null
      }
    },

    clearCachedPayData() {
      removeCachedPayData(this.getPayCacheKey())
    },

    handlePayData(payData) {
      if (!payData || !payData.payDataType) {
        return false
      }

      if (payData.payDataType === 'codeUrl' || payData.payDataType === 'codeImgUrl') {
        if (this.isQrPayMode) {
          setCachedPayData(this.getPayCacheKey(), payData)
        }
        this.qrCodeUrl = payData.payData
        this.qrCodeImageMode = payData.payDataType === 'codeImgUrl'
        this.payStatusText = '等待支付完成...'
        this.startStatusPolling()
        this.$nextTick(() => {
          this.renderQrCode()
        })
        return true
      }

      if (payData.payDataType === 'payurl') {
        this.payStatusText = '正在跳转微信支付页面...'
        window.location.href = payData.payData
        return true
      }

      if (payData.payDataType === 'form') {
        this.payStatusText = '正在提交支付...'
        this.submitPayForm(payData.payData)
        return true
      }

      return false
    },

    renderQrCode() {
      if (this.qrCodeImageMode || !this.qrCodeUrl || !this.$refs.qrcode) {
        return
      }
      this.$refs.qrcode.innerHTML = ''
      new QRCode(this.$refs.qrcode, {
        text: this.qrCodeUrl,
        width: 256,
        height: 256
      })
    },

    submitPayForm(formHtml) {
      const formWrapper = document.createElement('div')
      formWrapper.style.display = 'none'
      formWrapper.innerHTML = formHtml
      document.body.appendChild(formWrapper)
      const form = formWrapper.querySelector('form')
      if (form) {
        form.submit()
      }
    },

    startStatusPolling() {
      this.stopStatusPolling()
      this.statusPollTimer = setInterval(() => {
        this.refreshPayOrderStatus()
      }, 2000)
    },

    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer)
        this.statusPollTimer = null
      }
    },

    refreshPayOrderStatus() {
      getPayOrderInfo().then(res => {
        this.payOrderInfo = Object.assign({}, this.payOrderInfo, res)
        if (res.state === 2 || res.state === '2') {
          this.handlePaySuccess(res)
          return
        }
        if (res.state === 3 || res.state === '3' || res.state === 6 || res.state === '6') {
          this.stopStatusPolling()
          this.clearCachedPayData()
          this.payStatusText = res.errMsg || '订单状态异常'
        }
      }).catch(() => {
        this.payStatusText = '支付结果查询中...'
      })
    },

    buildReturnUrlWithOrderInfo(returnUrl) {
      if (!returnUrl) return returnUrl
      try {
        const url = new URL(returnUrl)
        if (this.payOrderInfo.mchOrderNo) {
          url.searchParams.set('bizOrderNo', this.payOrderInfo.mchOrderNo)
        }
        if (this.payOrderInfo.payOrderId) {
          url.searchParams.set('payOrderId', this.payOrderInfo.payOrderId)
        }
        return url.toString()
      } catch (e) {
        return returnUrl
      }
    },

    redirectAfterSuccess(returnUrl) {
      const targetUrl = this.buildReturnUrlWithOrderInfo(returnUrl)
      if (targetUrl) {
        window.setTimeout(() => {
          window.location.href = targetUrl
        }, 600)
      }
    },

    handlePaySuccess(orderInfo) {
      if (this.isSuccessHandled) {
        return
      }
      this.isSuccessHandled = true
      this.stopStatusPolling()
      this.clearCachedPayData()
      this.payStatusText = '支付成功，正在返回原页面...'
      this.redirectAfterSuccess(orderInfo && orderInfo.returnUrl)
    },

    onBridgeReady() {
      const that = this
      window.WeixinJSBridge.invoke(
        'getBrandWCPayRequest',
        JSON.parse(that.resData.payInfo),
        function (res) {
          if (res.err_msg === 'get_brand_wcpay_request:ok') {
            that.payStatusText = '支付成功，正在返回原页面...'
            if (that.payOrderInfo.returnUrl) {
              that.redirectAfterSuccess(that.payOrderInfo.returnUrl)
            } else {
              window.WeixinJSBridge.call('closeWindow')
            }
          }
          if (res.err_msg === 'get_brand_wcpay_request:cancel') {
            that.payStatusText = '用户已取消支付'
            alert('支付取消')
            window.WeixinJSBridge.call('closeWindow')
          }
          if (res.err_msg === 'get_brand_wcpay_request:fail') {
            that.payStatusText = '微信支付失败'
            alert('支付失败:' + JSON.stringify(res))
            window.WeixinJSBridge.call('closeWindow')
          }
          if (res.err_msg === 'total_fee') {
            that.payStatusText = '支付参数缺失'
            alert('缺少参数')
            window.WeixinJSBridge.call('closeWindow')
          }
        }
      )
    }
  }
}
</script>

<style scoped>
@import '../cashier-desktop.css';
</style>
